import { LIMIT_KEYS, LIMIT_LABELS, ROLE_LABELS, formatLimit, type WorkspaceDeletionReportDto, type WorkspaceRole } from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Ban, CalendarPlus, Download, Eye, Percent, PlayCircle, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/billing/status-badge";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errorMessage } from "@/lib/api";
import { billingApi, billingQueries, brl, shortDate } from "@/lib/billing-api";
import { formatDate } from "@/lib/utils";

export const Route = createFileRoute("/admin/workspaces_/$id")({
  component: AdminWorkspaceDetailPage,
});

type Action = "impersonate" | "trial" | "discount" | "suspend" | "reactivate" | "delete" | null;

function AdminWorkspaceDetailPage() {
  const { id } = Route.useParams();
  const detail = useQuery(billingQueries.adminWorkspace(id));
  const coupons = useQuery(billingQueries.adminCoupons());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [action, setAction] = useState<Action>(null);
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(7);
  const [couponId, setCouponId] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [report, setReport] = useState<WorkspaceDeletionReportDto | null>(null);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin"] });
  };
  const mutation = useMutation({
    mutationFn: async (a: Exclude<Action, null>) => {
      switch (a) {
        case "impersonate":
          await billingApi.impersonate(id, reason.trim() || undefined);
          return "Entrando como o cliente…";
        case "trial":
          await billingApi.extendTrial(id, days);
          return `Teste estendido em ${days} dias.`;
        case "discount":
          await billingApi.discount(id, couponId);
          return "Desconto aplicado.";
        case "suspend":
          await billingApi.suspend(id, reason.trim());
          return "Workspace suspenso (modo leitura).";
        case "reactivate":
          await billingApi.reactivate(id);
          return "Workspace reativado.";
        case "delete":
          setReport(await billingApi.deleteWorkspace(id, confirmName));
          return "Workspace excluído (LGPD).";
      }
    },
    onSuccess: async (msg, a) => {
      toast.success(msg);
      setAction(null);
      setReason("");
      if (a === "impersonate") {
        queryClient.clear();
        await navigate({ to: "/app/$workspaceId", params: { workspaceId: id } });
        return;
      }
      if (a !== "delete") await refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (report) {
    return (
      <>
        <PageHeader title="Workspace excluído" description={`${report.workspaceName} · ${formatDate(report.deletedAt)}`} />
        <Card>
          <CardHeader>
            <CardTitle>Relatório da exclusão (LGPD)</CardTitle>
            <CardDescription>Guardado em workspace_deletions (id {report.id}), sem dados pessoais.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <ul className="space-y-1">
              <li>Objetos apagados no R2: {report.r2ObjectsDeleted}</li>
              <li>Tokens Meta revogados: {report.metaTokensRevoked}</li>
              {report.metaRevokeErrors.map((e) => (
                <li key={e} className="text-destructive">
                  {e}
                </li>
              ))}
              <li>Contas de usuário apagadas (sem outro workspace): {report.usersDeleted}</li>
              <li>Stripe: {report.stripe}</li>
            </ul>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tabela</TableHead>
                  <TableHead className="text-right">Linhas apagadas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(report.rows)
                  .filter(([, n]) => n > 0)
                  .map(([t, n]) => (
                    <TableRow key={t}>
                      <TableCell className="font-mono text-xs">{t}</TableCell>
                      <TableCell className="text-right tabular-nums">{n}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <Button asChild>
              <Link to="/admin/workspaces">Voltar à lista</Link>
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  if (detail.isPending) return <LoadingState rows={6} />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  const d = detail.data;
  const paid = d.subscription.stripeSubscriptionId !== null && d.status !== "canceled";

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-2">
        <Link to="/admin/workspaces">
          <ArrowLeft /> Workspaces
        </Link>
      </Button>
      <PageHeader
        title={d.name}
        description={`${d.ownerEmail ?? "sem dono"} · criado em ${shortDate(d.createdAt)} · id ${d.id}`}
        actions={<StatusBadge row={d} />}
      />
      <div className="space-y-6">
        {d.suspended ? (
          <Alert variant="destructive">
            <AlertTitle>Suspenso</AlertTitle>
            <AlertDescription>{d.suspendedReason}</AlertDescription>
          </Alert>
        ) : null}
        <Card>
          <CardHeader>
            <CardTitle>Ações</CardTitle>
            <CardDescription>Toda ação pede confirmação e fica na auditoria do workspace.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button onClick={() => setAction("impersonate")}>
              <Eye /> Entrar como o cliente
            </Button>
            <Button variant="outline" onClick={() => setAction("trial")} disabled={paid}>
              <CalendarPlus /> Estender teste
            </Button>
            <Button variant="outline" onClick={() => setAction("discount")} disabled={!paid}>
              <Percent /> Dar desconto
            </Button>
            {d.suspended ? (
              <Button variant="outline" onClick={() => setAction("reactivate")}>
                <PlayCircle /> Reativar
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setAction("suspend")}>
                <Ban /> Suspender
              </Button>
            )}
            <Button asChild variant="outline">
              <a href={`/api/admin/workspaces/${d.id}/export`} download>
                <Download /> Exportar dados (LGPD)
              </a>
            </Button>
            <Button variant="destructive" onClick={() => setAction("delete")}>
              <Trash2 /> Excluir workspace
            </Button>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Plano e assinatura</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Plano:</span> {d.planName} ({d.cycle}) · MRR {brl(d.mrrCents)}
              </p>
              {d.trialEndsAt && d.status === "trialing" ? (
                <p>
                  <span className="text-muted-foreground">Teste até:</span> {shortDate(d.trialEndsAt)}
                </p>
              ) : null}
              <p>
                <span className="text-muted-foreground">Período atual até:</span> {shortDate(d.subscription.currentPeriodEnd)}
                {d.subscription.cancelAtPeriodEnd ? " (cancela no fim)" : ""}
              </p>
              {d.subscription.pastDueSince ? (
                <p>
                  <span className="text-muted-foreground">Pendente desde:</span> {formatDate(d.subscription.pastDueSince)}
                </p>
              ) : null}
              {d.subscription.discount ? (
                <p>
                  <span className="text-muted-foreground">Desconto:</span> {d.subscription.discount.code} ({d.subscription.discount.percentOff ? `${d.subscription.discount.percentOff}%` : brl(d.subscription.discount.amountOffCents)})
                </p>
              ) : null}
              <p className="font-mono text-xs text-muted-foreground">
                {d.subscription.stripeCustomerId ?? "sem customer"} · {d.subscription.stripeSubscriptionId ?? "sem assinatura no Stripe"}
              </p>
              <p>
                <span className="text-muted-foreground">IA em {d.ai.month}:</span> {d.ai.usedCredits} de {formatLimit(d.ai.monthlyCredits)} créditos · {d.ai.calls} chamadas · US$ {(d.ai.costUsdMicros / 1_000_000).toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Uso vs limites</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  {LIMIT_KEYS.map((k) => (
                    <TableRow key={k}>
                      <TableCell>{LIMIT_LABELS[k]}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {d.usage[k]} / {formatLimit(d.entitlements.limits[k])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Membros ({d.membersList.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableBody>
                  {d.membersList.map((m) => (
                    <TableRow key={m.userId}>
                      <TableCell>
                        {m.name}
                        <p className="text-xs text-muted-foreground">{m.email}</p>
                      </TableCell>
                      <TableCell className="text-right">{ROLE_LABELS[m.role as WorkspaceRole]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Conexões Meta</CardTitle>
            </CardHeader>
            <CardContent>
              {d.connections.length === 0 ? (
                <EmptyState title="Nenhuma conexão" description="O cliente ainda não conectou a Meta." />
              ) : (
                <Table>
                  <TableBody>
                    {d.connections.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>{c.status}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.adAccounts} contas</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Últimos erros da Meta</CardTitle>
          </CardHeader>
          <CardContent>
            {d.metaErrors.length === 0 ? (
              <EmptyState title="Nenhum erro recente" description="Sincronizações e escritas sem falhas registradas." />
            ) : (
              <Table>
                <TableBody>
                  {d.metaErrors.map((e) => (
                    <TableRow key={`${e.at}-${e.source}`}>
                      <TableCell className="whitespace-nowrap">{formatDate(e.at)}</TableCell>
                      <TableCell className="font-mono text-xs">{e.source}</TableCell>
                      <TableCell>{e.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Faturas</CardTitle>
            </CardHeader>
            <CardContent>
              {d.invoices.length === 0 ? (
                <EmptyState title="Nenhuma fatura" description="Ainda não houve cobrança." />
              ) : (
                <Table>
                  <TableBody>
                    {d.invoices.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell>{shortDate(i.createdAt)}</TableCell>
                        <TableCell>{brl(i.status === "paid" ? i.amountPaidCents : i.amountDueCents)}</TableCell>
                        <TableCell>{i.status === "paid" ? "Paga" : i.status === "open" ? "Em aberto" : i.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Acessos do suporte</CardTitle>
            </CardHeader>
            <CardContent>
              {d.impersonations.length === 0 ? (
                <EmptyState title="Nenhum acesso" description="Ninguém entrou como este cliente." />
              ) : (
                <Table>
                  <TableBody>
                    {d.impersonations.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell>{i.adminName ?? "—"}</TableCell>
                        <TableCell>{formatDate(i.startedAt)}</TableCell>
                        <TableCell>{i.endedAt ? `até ${formatDate(i.endedAt)}` : "em andamento"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={action === "impersonate"}
        onOpenChange={(o) => (!o ? setAction(null) : undefined)}
        title={`Entrar como ${d.name}?`}
        description="Você verá o app como um Admin deste workspace, com banner vermelho. Início, fim e cada ação ficam na auditoria do cliente com você como autor. Segredos e tokens continuam inacessíveis. Expira em 1 hora."
        confirmLabel="Entrar como o cliente"
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate("impersonate")}
      >
        <FormField id="motivo-imp" label="Motivo (opcional)">
          <Input id="motivo-imp" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: chamado #123" />
        </FormField>
      </ConfirmDialog>
      <ConfirmDialog
        open={action === "trial"}
        onOpenChange={(o) => (!o ? setAction(null) : undefined)}
        title="Estender o teste grátis"
        description="Os dias contam a partir do fim atual do teste (ou de hoje, se já acabou). Tira o workspace do modo leitura."
        confirmLabel={`Estender ${days} dias`}
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate("trial")}
      >
        <FormField id="dias" label="Dias">
          <Input id="dias" type="number" min={1} max={90} value={days} onChange={(e) => setDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))} />
        </FormField>
      </ConfirmDialog>
      <ConfirmDialog
        open={action === "discount"}
        onOpenChange={(o) => (!o ? setAction(null) : undefined)}
        title="Dar desconto"
        description="Aplica um cupom na assinatura (no Stripe em modo live)."
        confirmLabel="Aplicar"
        loading={mutation.isPending}
        onConfirm={() => (couponId ? mutation.mutate("discount") : toast.error("Escolha um cupom."))}
      >
        <select className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm" value={couponId} onChange={(e) => setCouponId(e.target.value)} aria-label="Cupom">
          <option value="">Escolha um cupom…</option>
          {coupons.data
            ?.filter((c) => c.active)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.percentOff ? `${c.percentOff}%` : brl(c.amountOffCents)} · {c.name}
              </option>
            ))}
        </select>
        {coupons.data?.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Crie um cupom em <Link to="/admin/cupons" className="underline">Cupons</Link>.
          </p>
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={action === "suspend"}
        onOpenChange={(o) => (!o ? setAction(null) : undefined)}
        title={`Suspender ${d.name}?`}
        description="O workspace entra em modo leitura (vê e exporta, não cria nem altera nada; regras param) até ser reativado."
        confirmLabel="Suspender"
        destructive
        loading={mutation.isPending}
        onConfirm={() => (reason.trim().length >= 3 ? mutation.mutate("suspend") : toast.error("Escreva o motivo."))}
      >
        <FormField id="motivo-susp" label="Motivo (aparece para o cliente no banner)">
          <Input id="motivo-susp" value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormField>
      </ConfirmDialog>
      <ConfirmDialog
        open={action === "reactivate"}
        onOpenChange={(o) => (!o ? setAction(null) : undefined)}
        title={`Reativar ${d.name}?`}
        confirmLabel="Reativar"
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate("reactivate")}
      />
      <ConfirmDialog
        open={action === "delete"}
        onOpenChange={(o) => (!o ? setAction(null) : undefined)}
        title="Excluir o workspace de vez (LGPD)"
        description="Apaga todos os dados do banco, os arquivos no R2, revoga os tokens da Meta, cancela a assinatura e apaga contas de usuário que só existiam por causa dele. Não dá para desfazer. Exporte os dados antes, se precisar."
        confirmLabel="Excluir definitivamente"
        destructive
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate("delete")}
      >
        <FormField id="confirm-nome" label={`Digite "${d.name}" para confirmar`}>
          <Input id="confirm-nome" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
        </FormField>
      </ConfirmDialog>
    </>
  );
}
